/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'

import { FormattedMessage } from '@modules/i18n'

import { ScopeType, type Budget } from '../../../types'
import DetailsSection from '../../../../components/DetailsSection'
import ScopeItem from '../../ScopeItem'

import CloudResourceDetails from './CloudResourceDetails'

const scopeHelpMessage = {
  [ScopeType.Organization]: {
    id: 'billing.notifications.budgets.budget-details.scope.organization.help',
    defaultMessage: 'The budget is scoped to the entire Elastic Cloud organization.',
  },
  [ScopeType.CloudResource]: {
    id: 'billing.notifications.budgets.budget-details.scope.cloud-resource.help',
    defaultMessage: 'The budget is scoped to 1 selected cloud resource.',
  },
}

type Props = {
  scopeType: Budget['scope_type']
  scopeValues: Budget['scope_values']
}

const Scope: FunctionComponent<Props> = ({ scopeType, scopeValues }) => {
  const isCloudResourceScope = scopeType === ScopeType.CloudResource

  return (
    <DetailsSection
      title={
        <FormattedMessage
          id='billing.notifications.budgets.budget-details.scope.title'
          defaultMessage='Scope'
        />
      }
      helpText={<FormattedMessage {...scopeHelpMessage[scopeType]} />}
    >
      {isCloudResourceScope ? (
        <CloudResourceDetails scopeValues={scopeValues} scopeType={scopeType} />
      ) : (
        <ScopeItem scopeValues={scopeValues} scopeType={scopeType} />
      )}
    </DetailsSection>
  )
}

export default Scope
